/*global Autodesk*/
import { AnalysisItem, AnalysisType, GroupType, projectAnalysis, ProjectAnalysisEvents, UnitType } from "@/types/analysis/ProjectAnalysis";
import { Viewing } from "@/types/LmViewer3D/Extensions/Viewing.Extension.Events";
import { ProgressIndicatorType } from "@/types/ProgressIndicator";
import { ViewerId, ViewerIds } from "@/types/SelectionManager";
import { useExportProjectData, useProjectData } from "@api/analysis";
import { ElementData, ExportFormatType, ExportSelectedDataRequest, FeatureManagementType, Model, ModelData, RebarEstimationStatus, Scenario, ScenarioData, ScenarioExport } from "@api/apiClient";

import { useCo2MaterialTypes } from "@api/co2MaterialTypes";
import { useProject } from "@api/projects";
import { useScenarios } from "@api/scenarios";
import { BarChartEvents } from "@components/Charts/Barchart.Events";
import SplitButton from "@components/SplitButton/SplitButton";
import useTotalCo2ConfigSync from "@features/analysis/hooks/useTotalCo2ConfigSync";
import { ChartOption } from "@features/analysis/types/chartOption";
import { ChartType } from "@features/analysis/types/chartType";
import useAppStore from "@hooks/useAppStore";
import useLocalStorage from "@hooks/useLocalStorage";
import { useMitt } from "@hooks/useMitt";
import i18n from "@i18n/i18n";
import CheckIcon from "@mui/icons-material/Check";
import { Box, FormControl, Grid, InputLabel, ListItemIcon, ListItemText, MenuItem, Select, Typography } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { findMapKey } from "@utils/mapUtils";
import { observer } from "mobx-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ChartOptionsToggle from "./ChartOptionsToggle";
import ByAttributesResults from "./Charts/ByAttributesResults/ByAttributesResults";
import ByCadMaterialsResults from "./Charts/ByCadMaterialsResutls/ByCadMaterialsResults";
import ByCategoriesResults from "./Charts/ByCategoriesResults/ByCategoriesResults";
import ByECMaterialsResults from "./Charts/ByECMaterialsResults/ByECMaterialsResults";
import ByTotalCO2eResults from "./Charts/ByTotalCO2eResults/ByTotalCO2eResults";
import GroupTypeToggle from "./GroupTypeToggle";
import { ResultsEvent, ResultsEvents } from "./ResultsEvents";
import ResultsGrid from "./ResultsGrid";

export interface ResultTabProps {
  onSelectChange: (scenarios: ScenarioData[]) => void;
}

export interface ResultTabOptions {
  exportOption: number;
}

const ResultsTab = observer((props: ResultTabProps) => {
  const appStore = useAppStore();
  const { emitter } = useMitt();
  const { id: projectId } = useParams();
  const [chartMode, setChartMode] = useState(projectAnalysis.analysisType);
  const [rebarEstimationStatus, setRebarEstimationStatus] = useState<RebarEstimationStatus | undefined>();
  const groupRef = useRef(null);
  const [groupType, setGroupType] = useState<GroupType>(GroupType.Scenario);
  const [chartType, setChartType] = useState<ChartType>(ChartType.BarChart);
  const [unitType, setUnitType] = useState<UnitType>(UnitType.tCO2e);
  const [chartOptions, setChartOptions] = useState<string[]>([]);
  const [selectedGroupKey, setSelectedGroupKey] = useState<string | undefined>();
  const resultsGridApiRef = useGridApiRef();
  const scenarios = useScenarios(projectId!);
  const exportProjectData = useExportProjectData(projectId!);
  const project = useProject(projectId!);
  const projectData = useProjectData(projectId!);
  const co2MaterialTypes = useCo2MaterialTypes();
  const chartModes: Partial<Record<AnalysisType, string>> = {
    [AnalysisType.CadModelCategory]: i18n.t("results-tab.title.categories"),
    [AnalysisType.CadMaterial]: i18n.t("results-tab.title.materials"),
    [AnalysisType.CadMaterialCategory]: i18n.t("results-tab.title.material-categories"),
    [AnalysisType.Co2MaterialType]: i18n.t("results-tab.title.co2-material-types"),
    [AnalysisType.Co2MaterialCategory]: i18n.t("results-tab.title.co2-material-categories"),
    [AnalysisType.ModelGroup]: i18n.t("results-tab.title.model-group"),
    [AnalysisType.Co2eIntensity]: i18n.t("results-tab.title.totalCo2"),
    [AnalysisType.System]: i18n.t("results-tab.title.system"),
    [AnalysisType.SubSystem]: i18n.t("results-tab.title.sub-system"),
    [AnalysisType.Attribute1]: i18n.t("results-tab.title.attribute1"),
    [AnalysisType.Attribute2]: i18n.t("results-tab.title.attribute2"),
    [AnalysisType.Attribute3]: i18n.t("results-tab.title.attribute3"),
  };
  useTotalCo2ConfigSync(resultsGridApiRef);

  const unitOptions = {
    [UnitType.tCO2e]: i18n.t("results-tab.title.co2e"),
    [UnitType.KgCO2ePerM2]: i18n.t("results-tab.title.unit-kgco2e-m2"),
    [UnitType.KgCO2ePerM2PerYear]: i18n.t("results-tab.title.unit-kgco2e-m2-year"),
  };

  const exportOptions = [
    {
      label: i18n.t("results-tab.export-options.default-excel-format"),
      value: ExportFormatType.DefaultExcelFormat,
    },
  ];

  if (appStore.hasFeature(FeatureManagementType.ExportToZero)) {
    exportOptions.push({
      label: i18n.t("results-tab.export-options.zero-platform-excel-format"),
      value: ExportFormatType.ZeroPlatformExcelFormat,
    });
  }

  const [resultTabOptions, setResultTabOptions] = useLocalStorage<ResultTabOptions>("results-tab-options", {
    exportOption: 0,
  });

  const updateChartOptions = async () => {
    const newChartOptions = [];
    if (appStore.showChart) {
      newChartOptions.push(ChartOption.ShowBarChart);
      setChartType(ChartType.BarChart);
    }
    if (appStore.showSankey) {
      newChartOptions.push(ChartOption.ShowSankey);
      setChartType(ChartType.Sankey);
    }
    if (appStore.showGrid) {
      newChartOptions.push(ChartOption.ShowGrid);
    }
    setChartOptions(newChartOptions);
  };

  const updateRebarEstimationStatus = async () => {
    if (scenarios.data) {
      let status = scenarios.data?.some((s: Scenario) => s.rebarEstimationStatus === RebarEstimationStatus.Enabled)
        ? RebarEstimationStatus.Enabled
        : RebarEstimationStatus.Disabled;
      status = scenarios.data?.some((s: Scenario) => s.rebarEstimationStatus === RebarEstimationStatus.Estimated) ? RebarEstimationStatus.Estimated : status;
      setRebarEstimationStatus(status);
    } else if (projectData.data) {
      const status = project.data?.project?.rebarEstimationStatus;
      setRebarEstimationStatus(status);
    }
  };

  const handleGridSelection = useCallback(async () => {
    if (projectData.data) {
      const selectedScenarioIds = appStore.selectionManager.scenarioIds;
      const scenarioDatas: ScenarioData[] = projectData.data?.scenarioDatas?.filter((s: ScenarioData) => selectedScenarioIds.includes(s.scenarioId!)) || [];
      props?.onSelectChange(scenarioDatas);

      projectAnalysis.updateAnalysis(appStore.selectionManager, projectData.data, co2MaterialTypes.data);
      if (appStore.showSankey) {
        projectAnalysis.updateSankeyAnalysis(appStore.selectionManager, projectData.data, co2MaterialTypes.data, appStore.sankeyOptions);
      }
      await applyTheming();
      await updateRebarEstimationStatus();
    }
  }, [projectData.data, appStore.selectionManager.scenarioIds, co2MaterialTypes.data, appStore.showSankey, appStore.sankeyOptions, props]);

  const applyTheming = async () => {
    Object.values(appStore.selectionManager.selections).forEach(async (selection) => {
      selection.urns.forEach(async (urn: string) => {
        const elementData = findElementData(selection.viewerId, urn);
        if (elementData) {
          try {
            if (selection.viewerId === "viewer1") {
              await appStore.lmViewer1?.applyTheming(urn, elementData as any);
            }
            if (selection.viewerId === "viewer2") {
              await appStore.lmViewer2?.applyTheming(urn, elementData as any);
            }
          } catch (e) {
            console.log(e);
          }
        }
      });
    });
  };

  /*
   * Handle the selection of a TotalCo2 bucket from the viewer extension
   */
  const handleTotalCo2Selection = async (viewerIndex?: number, colIndex?: number | undefined, legend?: string | undefined) => {
    console.log(`handleTotalCo2Selection: ${colIndex}, ${legend}`);
    // TODO: this code is duplicated in ByTotalCO2eResults.tsx
    if (legend) {
      const scenarioId = appStore.selectionManager.getScenarioIdByViewerIndex(viewerIndex!);
      if (scenarioId) {
        const scenarioAnalysis = projectAnalysis.getAnalysis(AnalysisType.Co2eIntensity)?.scenarioAnalyses.get(scenarioId);
        if (scenarioAnalysis) {
          const groupKey = findMapKey(scenarioAnalysis.co2eIntensity.items, (item: AnalysisItem) => item.name === legend);
          scenarioAnalysis.modelGroupCadIdsMap.forEach((groupCadIds, urn) => {
            (async () => {
              const viewerId = appStore.selectionManager.getViewerIdByScenarioId(scenarioAnalysis.scenarioId)!;
              if (viewerId === ViewerIds.Viewer1) {
                await appStore.lmViewer1?.isolateByCadIds(groupCadIds.get(groupKey as string) || [], urn);
              } else {
                await appStore.lmViewer2?.isolateByCadIds(groupCadIds.get(groupKey as string) || [], urn);
              }
            })();
          });
        }
      }
    } else {
      await appStore.lmViewer1?.resetView();
      await appStore.lmViewer2?.resetView();
    }
  };

  const findElementData = (viewerId: ViewerId, urn: string): Record<string, ElementData> | undefined => {
    let result;
    if (urn) {
      const scenarioId = appStore.selectionManager.getScenarioIdByViewerId(viewerId);
      const scenarioData = projectData.data?.scenarioDatas?.find((scenarioData: ScenarioData) => scenarioData.scenarioId === scenarioId);
      scenarioData?.modelDatas?.forEach((modelData: ModelData) => {
        if (modelData.model?.apsViewId === urn) {
          result = modelData.elementDatas;
        }
      });
    }
    return result;
  };

  const handleChartMode = async (event: any) => {
    console.log("setting chart mode: ", event.target.value);

    projectAnalysis.setAnalysisType(event.target.value as AnalysisType, appStore.selectionManager, projectData.data, co2MaterialTypes.data);
    projectAnalysis.addSelectionsFromModelToCadIdMap(projectAnalysis.analysisType, appStore.selectionManager, projectData.data);

    emitter.emit(BarChartEvents.BAR_CHART_SELECTED_EVENT, { colIndex: 0, legend: 0 });
    setChartMode(event.target.value);
  };

  const handleUnitChange = async (event: any) => {
    console.log("setting unit type: ", event.target.value);
    const newUnit = event.target.value as UnitType;
    projectAnalysis.setUnitType(newUnit);
    setUnitType(newUnit);
    projectAnalysis.updateSankeyAnalysis(appStore.selectionManager, projectData.data, co2MaterialTypes.data, appStore.sankeyOptions);
  };

  const handleChartOptionsChange = async (_event: any, options: string[]) => {
    // TODO: name these better setShowResultsChart, setShowResultsGrid
    appStore.setShowGrid(options?.includes(ChartOption.ShowGrid));

    if (options?.includes(ChartOption.ShowBarChart) && options?.includes(ChartOption.ShowSankey)) {
      appStore.toggleShowChart();
      appStore.toggleShowSankey();
    } else {
      appStore.setShowChart(options?.includes(ChartOption.ShowBarChart));
      appStore.setShowSankey(options?.includes(ChartOption.ShowSankey));
    }
    setChartType(appStore.showChart ? ChartType.BarChart : ChartType.Sankey);

    await updateChartOptions();
  };

  const handleAnalysisGroupType = async (_event: any, value: string | null) => {
    // NOTE: projectAnalysis isn't using mobx, so we need local state to trigger re-renders
    setGroupType(value as GroupType);
    projectAnalysis.setGroupType(value as GroupType, appStore.selectionManager, projectData.data, co2MaterialTypes.data);
  };

  const handleExportSelectedData = async (option: any, _index: number, _event: React.MouseEvent<HTMLButtonElement>) => {
    try {
      const selectedScenarios = scenarios.data?.filter((s: Scenario) => appStore.selectionManager.scenarioIds.includes(s.scenarioId!)) || [];
      const exportSelectedDataRequest = new ExportSelectedDataRequest({
        exportFormat: option.value as ExportFormatType,
        scenarioExports: selectedScenarios.map((s: Scenario) => {
          const modelIds = appStore.getSelectedModels(s.scenarioId!).map((m: Model) => m.modelId!);

          return new ScenarioExport({
            scenarioId: s.scenarioId,
            modelIds,
          });
        }),
      });
      const response = await exportProjectData.mutateAsync(exportSelectedDataRequest);

      appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.ExcelExport, true);
    } catch (e) {
      // TODO: report error
    }
  };


  useEffect(() => {
    const go = async () => {
      await appStore.selectionManager.setMode("DualScenarios");
      await updateChartOptions();

      //Set the selected unit type in projectAnalysis to match the component's default state
      projectAnalysis.setUnitType(unitType);
    };
    go();
  }, []);

  useEffect(() => {
    const go = async () => {
      await handleGridSelection();
      await updateRebarEstimationStatus();
    };

    go();
  }, [projectData.data]);

  useEffect(() => {
    const go = async () => {
      await updateRebarEstimationStatus();
    };

    go();
  }, [scenarios.data]);

  /**
   * When the user changes the ResultsGrid A-D column visibility it will trigger a change in the ProjectAnalysis
   * totalCo2Confi which will then fire the CONFIGURATION_CHANGED_EVENT. That's an indication that we need to
   * perform a re-evaluation of the chart calculations.
   */
  useEffect(() => {
    projectAnalysis.emitter.on(ProjectAnalysisEvents.CONFIGURATION_CHANGED_EVENT, handleGridSelection);

    return () => {
      projectAnalysis.emitter.off(ProjectAnalysisEvents.CONFIGURATION_CHANGED_EVENT, handleGridSelection);
    };
  }, [handleGridSelection]);

  /**
   * Handles the event when the viewer selection changes.
   *
   * @param modelToCadIdMap - A map where the key is a model ID and the value is a set of CAD IDs.
   *                          If undefined, selection is reset.
   *
   * event.selections[].dbIdArray contains the selected dbIds which we must map to cadIds
   * event.selections[].model is the viewer model
   * event.selections[].model.myData.urn is the urn which matches to projectData.scenarioData[].modelData[].model.apsViewId
   *
   * appStore.selectionManager knows which scenario and models are active for viewer1
   * Given the scenarioId and modelId, we need to convert the cadIds to CadModelCategory, CadMaterial, Co2MaterialType, or Co2eIntensity
   * Once we have a list of ElementData items, we need send a message to the BarChart, DataGrid, Sankey
   * diagram etc. to select based on
   *
   * BarChart will need to lookup based on:
   * 1. CadModelCategory => ElementData.cadModelCategoryId
   * 2. CadMaterial => ElementData.cadMaterial
   * 3. Co2MaterialTypeId => or ElementData.co2MaterialTypeId
   * 4. Co2eIntensity => ElementData.totalCo2 => bucket lookup will provide an index
   *
   * DataGrid will need to lookup based on:
   * 1. CadModelCategory => ElementData.cadModelCategoryId => DataGrid.rows.groupKey
   * 2. CadMaterial => ElementData.cadMaterial => DataGrid.rows.groupKey
   * 3. Co2MaterialTypeId => or ElementData.co2MaterialTypeId => DataGrid.rows.groupKey
   * 4. Co2eIntensity => ElementData.totalCo2 => bucket lookup will provide an index => DataGrid.rows.groupKey
   *
   * Sankey diagram will need to lookup based on:
   * 1. CadModelCategory => ElementData.cadModelCategoryId => SankeyDiagram.nodes.id
   * 2. CadMaterial => ElementData.cadMaterial => SankeyDiagram.nodes.id
   * 3. Co2MaterialTypeId => or ElementData.co2MaterialTypeId => SankeyDiagram.nodes.id
   */
  const handleViewerSelectionChanged = async (modelToCadIdMap: Map<string, Set<string>> | undefined) => {
    if (modelToCadIdMap) {
      console.log("modelToCadIdMap: ", modelToCadIdMap);
      projectAnalysis.addSelectionsFromModelToCadIdMap(projectAnalysis.analysisType, appStore.selectionManager, projectData.data, modelToCadIdMap);
    }
  };

  /*
   * Save the selected groupKey to state
   * and re-send the selection event once the GEOMETRY_LOADED_EVENT is received
   * to update the viewer selection
   */
  useEffect(() => {
    const handleUpdateSelection = async (event: any) => {
      console.log("ResultsTab: handleUpdateSelection: ", event);
      setSelectedGroupKey(event.groupKey);
    };

    emitter.on(ResultsEvents.SELECTION_CHANGED, handleUpdateSelection);

    return () => {
      emitter.off(ResultsEvents.SELECTION_CHANGED, handleUpdateSelection);
    };
  }, []);

  // Viewer1 events
  useEffect(() => {
    const handleGeometryLoaded = async (event: any) => {
      console.log("GEOMETRY_LOADED_EVENT: ", event);

      if (selectedGroupKey) {
        const newEvent: ResultsEvent = {
          key: "geometry-loaded",
          eventType: ResultsEvents.SELECTION_CHANGED,
          groupKey: selectedGroupKey,
        };
        emitter.emit(ResultsEvents.SELECTION_CHANGED, newEvent);
      }

      await appStore.lmViewer1?.applyTheming(event.model.myData.urn, findElementData(ViewerIds.Viewer1, event.model.myData.urn));

      const selections = appStore.lmViewer1?.getAggregateSelections();
      const modelToCadIdMap = await appStore.lmViewer1?.getCadIdsFromSelections(selections);
      await handleViewerSelectionChanged(modelToCadIdMap);
    };
    const handleAggregateSelectionChanged = async (event: any) => {
      console.log("AGGREGATE_SELECTION_CHANGED_EVENT: ", event);

      const modelToCadIdMap = await appStore.lmViewer1?.getCadIdsFromSelections(event.selections);
      await handleViewerSelectionChanged(modelToCadIdMap);
    };
    const handleModelUnloaded = async (event: any) => {
      console.log("MODEL_UNLOADED_EVENT: ", event);

      const selections = appStore.lmViewer1?.getAggregateSelections();
      const modelToCadIdMap = await appStore.lmViewer1?.getCadIdsFromSelections(selections);
      await handleViewerSelectionChanged(modelToCadIdMap);
    };
    const handleTotalCo2SelectionChanged = async (event: any) => {
      console.log("TOTALCO2_SELECTION_CHANGED_EVENT: ", event);
      await handleTotalCo2Selection(event.viewerIndex, event.index, event.legend);

      const newEvent: ResultsEvent = {
        key: "total-co2-extension",
        eventType: ResultsEvents.SELECTION_CHANGED,
        viewerIndex: event.viewerIndex,
        groupKey: event.index,
      };

      emitter.emit(ResultsEvents.SELECTION_CHANGED, newEvent);
    };

    if (appStore.lmViewer1) {
      appStore.lmViewer1.emitter.on(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, handleGeometryLoaded);
      appStore.lmViewer1.emitter.on(Autodesk.Viewing.AGGREGATE_SELECTION_CHANGED_EVENT, handleAggregateSelectionChanged);
      appStore.lmViewer1.emitter.on(Autodesk.Viewing.MODEL_UNLOADED_EVENT, handleModelUnloaded);
      appStore.lmViewer1.emitter.on(Viewing.Extensions.TOTALCO2_SELECTION_CHANGED_EVENT, handleTotalCo2SelectionChanged);
    }
    return () => {
      if (appStore.lmViewer1) {
        appStore.lmViewer1.emitter.off(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, handleGeometryLoaded);
        appStore.lmViewer1.emitter.off(Autodesk.Viewing.AGGREGATE_SELECTION_CHANGED_EVENT, handleAggregateSelectionChanged);
        appStore.lmViewer1.emitter.off(Autodesk.Viewing.MODEL_UNLOADED_EVENT, handleModelUnloaded);
        appStore.lmViewer1.emitter.off(Viewing.Extensions.TOTALCO2_SELECTION_CHANGED_EVENT, handleTotalCo2SelectionChanged);
      }
    };
  }, [appStore.lmViewer1, projectAnalysis.analysisType, appStore.selectionManager, projectData.data, selectedGroupKey]);

  // Viewer2 events
  useEffect(() => {
    const handleGeometryLoaded = async (event: any) => {
      console.log("GEOMETRY_LOADED_EVENT: ", event);

      if (selectedGroupKey) {
        const newEvent: ResultsEvent = {
          key: "geometry-loaded",
          eventType: ResultsEvents.SELECTION_CHANGED,
          groupKey: selectedGroupKey,
        };
        emitter.emit(ResultsEvents.SELECTION_CHANGED, newEvent);
      }

      await appStore.lmViewer2?.applyTheming(event.model.myData.urn, findElementData(ViewerIds.Viewer2, event.model.myData.urn));

      const selections = appStore.lmViewer2?.getAggregateSelections();
      const modelToCadIdMap = await appStore.lmViewer2?.getCadIdsFromSelections(selections);
      await handleViewerSelectionChanged(modelToCadIdMap);
    };
    const handleAggregateSelectionChanged = async (event: any) => {
      console.log("AGGREGATE_SELECTION_CHANGED_EVENT: ", event);

      const modelToCadIdMap = await appStore.lmViewer2?.getCadIdsFromSelections(event.selections);
      await handleViewerSelectionChanged(modelToCadIdMap);
    };
    const handleModelUnloaded = async (event: any) => {
      console.log("MODEL_UNLOADED_EVENT: ", event);

      const selections = appStore.lmViewer2?.getAggregateSelections();
      const modelToCadIdMap = await appStore.lmViewer2?.getCadIdsFromSelections(selections);
      await handleViewerSelectionChanged(modelToCadIdMap);
    };
    const handleTotalCo2SelectionChanged = async (event: any) => {
      console.log("TOTALCO2_SELECTION_CHANGED_EVENT: ", event);
      await handleTotalCo2Selection(event.viewerIndex, event.index, event.legend);

      const newEvent: ResultsEvent = {
        key: "total-co2-extension",
        eventType: ResultsEvents.SELECTION_CHANGED,
        viewerIndex: event.viewerIndex,
        groupKey: event.index,
      };

      emitter.emit(ResultsEvents.SELECTION_CHANGED, newEvent);
    };

    if (appStore.lmViewer2) {
      appStore.lmViewer2.emitter.on(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, handleGeometryLoaded);
      appStore.lmViewer2.emitter.on(Autodesk.Viewing.AGGREGATE_SELECTION_CHANGED_EVENT, handleAggregateSelectionChanged);
      appStore.lmViewer2.emitter.on(Autodesk.Viewing.MODEL_UNLOADED_EVENT, handleModelUnloaded);
      appStore.lmViewer2.emitter.on(Viewing.Extensions.TOTALCO2_SELECTION_CHANGED_EVENT, handleTotalCo2SelectionChanged);
    }
    return () => {
      if (appStore.lmViewer2) {
        appStore.lmViewer2.emitter.off(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, handleGeometryLoaded);
        appStore.lmViewer2.emitter.off(Autodesk.Viewing.AGGREGATE_SELECTION_CHANGED_EVENT, handleAggregateSelectionChanged);
        appStore.lmViewer2.emitter.off(Autodesk.Viewing.MODEL_UNLOADED_EVENT, handleModelUnloaded);
        appStore.lmViewer2.emitter.off(Viewing.Extensions.TOTALCO2_SELECTION_CHANGED_EVENT, handleTotalCo2SelectionChanged);
      }
    };
  }, [appStore.lmViewer2, projectAnalysis.analysisType, appStore.selectionManager, projectData.data, selectedGroupKey]);

  useEffect(() => {
    projectAnalysis.updateSankeyAnalysis(appStore.selectionManager, projectData.data, co2MaterialTypes.data, appStore.sankeyOptions);
  }, [appStore.sankeyOptions]);
  // Whether attribute-based analysis is available
  const attributesFeatureEnabled = appStore.hasFeature(FeatureManagementType.ResultsAttributes);
  return (
    <Box mt={1} sx={{ display: "flex", flexDirection: "column", height: "calc(100% - 52px)", gap: 1, width: "100%" }}>
      <Box sx={{ float: "left", position: "absolute", top: -32, right: 4 }}>
        {rebarEstimationStatus === RebarEstimationStatus.Enabled && <Typography variant="body2">{i18n.t("results-tab.title.rebar-estimation-enabled")}</Typography>}
        {rebarEstimationStatus === RebarEstimationStatus.Estimated && <Typography variant="body2">{i18n.t("results-tab.title.rebar-estimation-status")}</Typography>}
      </Box>
      <Box sx={{ flexShrink: 0, minWidth: "100%" }}>
        <ResultsGrid onSelect={handleGridSelection} apiRef={resultsGridApiRef} />
      </Box>
      <Grid container spacing={2} sx={{ alignItems: "center" }}>
        <Grid size="grow">
          <Box sx={{ display: "flex", width: "100%" }}>
            <SplitButton
              options={exportOptions}
              defaultValue={exportOptions[resultTabOptions.exportOption]}
              loading={exportProjectData.isPending || appStore.progressIndicator.getShowProgressIndicator(ProgressIndicatorType.ExcelExport)}
              variant="contained"
              onClick={handleExportSelectedData}
              onChange={(_option: any, index: number) => setResultTabOptions("exportOption", index)}
              getOptionLabel={(option: any) => option.label}
            >
              {i18n.t("results-tab.export-button.title")}
            </SplitButton>
          </Box>
        </Grid>
        {appStore.hasFeature(FeatureManagementType.AdditionalMetadata) && (
          <Grid size="auto">
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FormControl variant="standard">
                <InputLabel id="unit-select-label">{i18n.t("results-tab.title.unit")}</InputLabel>
                <Select
                  labelId="unit-select-label"
                  id="unit-select"
                  value={unitType}
                  onChange={handleUnitChange}
                  sx={{ minWidth: 150 }}
                  size="small"
                >
                  {Object.entries(unitOptions).map(([key, label]) => (
                    <MenuItem key={key} value={key}>

                      <ListItemText primary={label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>
        )}
        <Grid size="auto">
          <ChartOptionsToggle options={chartOptions} onChange={handleChartOptionsChange} />
        </Grid>
        <Grid size="auto">
          <GroupTypeToggle value={groupType} onChange={handleAnalysisGroupType} />
        </Grid>
        <Grid size="auto" mr={1}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FormControl ref={groupRef} variant="standard">
              <InputLabel id="group-select-label">{i18n.t("results-tab.title.group-by")}</InputLabel>
              <Select
                labelId="group-select-label"
                id="group-select"
                value={chartMode}
                onChange={(e) => handleChartMode({ target: { value: e.target.value } } as any)}
                sx={{ minWidth: 120 }}
                size="small"
                renderValue={(selected) => <ListItemText primary={chartModes[selected]} />}
              >
                <MenuItem value={AnalysisType.CadModelCategory}>
                  <ListItemIcon>{chartMode === AnalysisType.CadModelCategory && <CheckIcon />}</ListItemIcon>
                  <ListItemText primary={i18n.t("results-tab.title.categories")} />
                </MenuItem>
                <MenuItem value={AnalysisType.CadMaterial}>
                  <ListItemIcon>{chartMode === AnalysisType.CadMaterial && <CheckIcon />}</ListItemIcon>
                  <ListItemText primary={i18n.t("results-tab.title.materials")} />
                </MenuItem>
                <MenuItem value={AnalysisType.Co2MaterialType}>
                  <ListItemIcon>{chartMode === AnalysisType.Co2MaterialType && <CheckIcon />}</ListItemIcon>
                  <ListItemText primary={i18n.t("results-tab.title.co2-material-types")} />
                </MenuItem>
                <MenuItem value={AnalysisType.Co2eIntensity}>
                  <ListItemIcon>{chartMode === AnalysisType.Co2eIntensity && <CheckIcon />}</ListItemIcon>
                  <ListItemText primary={i18n.t("results-tab.title.totalCo2")} />
                </MenuItem>

                {attributesFeatureEnabled && (
                  <MenuItem value={AnalysisType.System}>
                    <ListItemIcon>{chartMode === AnalysisType.System && <CheckIcon />}</ListItemIcon>
                    <ListItemText primary={i18n.t("results-tab.title.system")} />
                  </MenuItem>
                )}

                {attributesFeatureEnabled && (
                  <MenuItem value={AnalysisType.SubSystem}>
                    <ListItemIcon>{chartMode === AnalysisType.SubSystem && <CheckIcon />}</ListItemIcon>
                    <ListItemText primary={i18n.t("results-tab.title.sub-system")} />
                  </MenuItem>
                )}

                {attributesFeatureEnabled && (
                  <MenuItem value={AnalysisType.Attribute1}>
                    <ListItemIcon>{chartMode === AnalysisType.Attribute1 && <CheckIcon />}</ListItemIcon>
                    <ListItemText primary={i18n.t("results-tab.title.attribute1")} />
                  </MenuItem>
                )}

                {attributesFeatureEnabled && (
                  <MenuItem value={AnalysisType.Attribute2}>
                    <ListItemIcon>{chartMode === AnalysisType.Attribute2 && <CheckIcon />}</ListItemIcon>
                    <ListItemText primary={i18n.t("results-tab.title.attribute2")} />
                  </MenuItem>
                )}

                {attributesFeatureEnabled && (
                  <MenuItem value={AnalysisType.Attribute3}>
                    <ListItemIcon>{chartMode === AnalysisType.Attribute3 && <CheckIcon />}</ListItemIcon>
                    <ListItemText primary={i18n.t("results-tab.title.attribute3")} />
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Box>
        </Grid>
      </Grid>
      <Box sx={{ display: "flex", flexGrow: 1, position: "relative", width: "100%" }}>
        <Box sx={{ width: "100%", height: "100%" }}>
          {chartMode === AnalysisType.CadModelCategory && <ByCategoriesResults emitter={emitter} chartType={chartType} />}
          {chartMode === AnalysisType.CadMaterial && <ByCadMaterialsResults emitter={emitter} chartType={chartType} />}
          {chartMode === AnalysisType.Co2MaterialType && <ByECMaterialsResults emitter={emitter} chartType={chartType} />}
          {chartMode === AnalysisType.Co2eIntensity && <ByTotalCO2eResults emitter={emitter} chartType={chartType} />}

          {attributesFeatureEnabled && ( // only render attributes-related view if feature enabled
            <>
              {(chartMode === AnalysisType.System ||
                chartMode === AnalysisType.SubSystem ||
                chartMode === AnalysisType.Attribute1 ||
                chartMode === AnalysisType.Attribute2 ||
                chartMode === AnalysisType.Attribute3) && (
                  // Pass the selected analysis type so child can decide whether it's system/subsystem/attribute
                  <ByAttributesResults emitter={emitter} chartType={chartType} attributeType={chartMode} />
                )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
});

export default ResultsTab;
